import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navigation } from '../data';
import { useCms } from '../context/CmsContext';

export default function Navbar() {
  const { siteSettings } = useCms();
  const [isOpen, setIsOpen] = useState(false);
  const [isYdelserOpen, setIsYdelserOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsYdelserOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isUnder1200 = windowWidth < 1200;
  const isLightNavbar = location.pathname !== '/' || isScrolled || isOpen;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-zinc-200 py-4 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={siteSettings.logo || "https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png"}
            alt="Ecolaser Logo"
            className="h-14 md:h-18 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (Strictly >= 1200px) */}
        {!isUnder1200 && (
          <nav className="flex items-center gap-3 xl:gap-4 2xl:gap-5">
            {navigation.menu.map((item) => {
              if (item.children) {
                return (
                  <div
                    key={item.label}
                    className="relative group"
                    onMouseEnter={() => setIsYdelserOpen(true)}
                    onMouseLeave={() => setIsYdelserOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1 text-[13px] font-semibold tracking-wide uppercase transition-colors duration-200 cursor-pointer ${
                        isActive(item.href)
                          ? 'text-zinc-950 font-bold'
                          : 'text-zinc-600 hover:text-zinc-950'
                      }`}
                    >
                      {item.label}
                      <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${isYdelserOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown */}
                    <div
                      className={`absolute top-full left-0 mt-2 w-72 rounded-lg shadow-2xl py-3 transition-all duration-200 bg-white border border-zinc-200 ${
                        isYdelserOpen
                          ? 'opacity-100 visible translate-y-0'
                          : 'opacity-0 invisible -translate-y-2'
                      }`}
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className={`block px-5 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors duration-200 ${
                            location.pathname === child.href
                              ? 'text-zinc-950 bg-zinc-100/50 font-bold'
                              : 'text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`text-[13px] font-semibold tracking-wide uppercase transition-colors duration-200 whitespace-nowrap ${
                    isActive(item.href)
                      ? 'text-zinc-950 font-bold'
                      : 'text-zinc-600 hover:text-zinc-950'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {/* Header CTA */}
            <Link
              to="/kontakt"
              className="bg-brand-green hover:bg-brand-green-light text-white font-bold px-4 py-2 rounded-md text-xs uppercase tracking-wider transition-all duration-300 hover:shadow-[0_4px_12px_rgba(118,184,42,0.3)] whitespace-nowrap"
            >
              Få et tilbud
            </Link>
          </nav>
        )}

        {/* Mobile Menu Button (Strictly < 1200px) */}
        {isUnder1200 && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none p-2 cursor-pointer transition-colors text-zinc-800 hover:text-brand-green"
            aria-label="Toggle menu"
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
          </button>
        )}
      </div>

      {/* Mobile Menu Overlay (Strictly < 1200px) */}
      {isUnder1200 && isOpen && (
        <div className="fixed inset-0 top-[73px] z-40 overflow-y-auto border-t flex flex-col bg-white border-zinc-200">
          <div className="flex-1 px-6 py-8 flex flex-col gap-6">
            {navigation.menu.map((item) => {
              if (item.children) {
                return (
                  <div key={item.label} className="flex flex-col gap-3">
                    <button
                      onClick={() => setIsYdelserOpen(!isYdelserOpen)}
                      className="flex items-center justify-between text-left text-lg font-bold tracking-wide uppercase transition-colors text-zinc-950 hover:text-brand-green"
                    >
                      {item.label}
                      <i className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${isYdelserOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isYdelserOpen && (
                      <div className="pl-4 border-l-2 flex flex-col gap-3 mt-1 border-zinc-200">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={`text-sm font-semibold tracking-wider uppercase transition-colors duration-200 ${
                              location.pathname === child.href
                                ? 'text-zinc-950 font-bold'
                                : 'text-zinc-600 hover:text-zinc-950'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`text-lg font-bold tracking-wide uppercase transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-zinc-950'
                      : 'text-zinc-600 hover:text-zinc-950'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <div className="mt-8 pt-8 border-t flex flex-col gap-4 border-zinc-200">
              <Link
                to="/kontakt"
                className="w-full bg-brand-green hover:bg-brand-green-light text-white font-bold py-3 rounded-md text-center text-sm uppercase tracking-wider transition-all duration-300"
              >
                Få et tilbud
              </Link>
              <a
                href={siteSettings.phoneTel}
                className="flex items-center justify-center gap-2 font-semibold py-2 transition-colors text-zinc-700 hover:text-brand-green"
              >
                <i className="fa-solid fa-phone text-brand-green" />
                Tlf: {siteSettings.phone}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
